<template>
  <div class="campaign-data">
    <div class="campaign-data__mail-info">
      <div class="created">
        <p class="title">Date Created</p>
        <p class="data">
          <span class="selected">{{ created.dataOnly }} </span
          ><span> at {{ created.timeOnly }}</span>
        </p>
      </div>
      <div class="mail-status">
        <p class="title">{{ getMailStatusInfo.label }}</p>
        <p class="data">
          <span class="selected">{{ getMailStatusInfo.date }} </span>
          <span> at {{ getMailStatusInfo.time }}</span>
        </p>
      </div>
      <div v-if="!template.deleted_at" class="template-complex-type">
        <p class="title">Type</p>
        <p class="data">
          <span class="selected">{{ complexType }}</span>
        </p>
      </div>
      <div v-else class="deleted">
        <p class="title">Template Deleted At</p>
        <p class="data">
          <span class="selected">{{ template.deleted_at.dataOnly }} </span
          ><span> at {{ template.deleted_at.timeOnly }}</span>
        </p>
      </div>
    </div>

    <div class="campaign-data__labels">
      <div class="campaign-name">
        <span class="selected">{{ name }}</span>
      </div>
      <div class="template-item" :class="{ ['deleted']: template.deleted_at }">
        Template:
        <VLink
          v-if="!template.deleted_at"
          :label="template.name"
          type="primary"
          :routerLink="_builderLink"
          extraClass="campaign-details"
        ></VLink>
        <label v-else>
          {{ template.name }}
          (deleted)
        </label>
      </div>
    </div>
    <div class="campaign-data__tags-wrapper">
      <div v-if="tags.length" :ref="rootRefElement" id="tags">
        <VTag
          :ref="tag"
          v-for="tag in tags"
          :key="tag"
          :_key="tag"
          :label="tag"
          :withDelete="false"
        />
      </div>
      <div v-if="hiddenTags.length" id="hidden-tags">
        and {{ hiddenTags.length }} more
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import VTag from "@/components/VTag";

import { capitalizeFirstLetter } from "@/utils/util";

//mixins
import withIntersectionObserver from "@/mixins/withIntersectionObserver";

export default Vue.component("CampaignData", {
  props: {
    name: VueTypes.string.isRequired,
    created: VueTypes.object,
    mailed: VueTypes.object,
    updated: VueTypes.object,
    accepted: VueTypes.object,
    template: VueTypes.shape({
      id: VueTypes.any,
      name: VueTypes.string,
      deleted_at: VueTypes.any
    }).isRequired,
    complexType: VueTypes.string,
    campaignType: VueTypes.oneOf(["letter", "postcard"]),
    tags: VueTypes.array
  },

  components: {
    VTag
  },

  mixins: [withIntersectionObserver("tags", "hiddenTags", "rootRefName")],

  data() {
    return {
      hiddenTags: [],
      rootRefElement: "tags"
    };
  },

  computed: {
    _builderLink() {
      const prefix =
        this.campaignType === "letter"
          ? "letters-templates"
          : "postcards-templates";
      return `/${prefix}/settings/${this.template.id}?type=${this.campaignType}`;
    },

    getMailStatusInfo() {
      let key = "";
      if (this.mailed) {
        key = "mailed";
      } else if (this.accepted) {
        key = "processing";
      } else {
        key = "updated";
      }
      return {
        label: `${capitalizeFirstLetter(key)} At`,
        date: this[key].dataOnly,
        time: this[key].timeOnly
      };
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "campaign-data";

.#{$ns} {
  flex: 1;
  min-width: 310px;
  height: 222px;
  padding: 30px;
  margin-right: 30px;
  box-shadow: 2px 4px 25px rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  font-family: Lato-Regular, Arial, Helvetica, "sans-serif";
}

.#{$ns}__mail-info {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  font-size: 12px;
  line-height: 1.2;
  color: $tertiary-text-color;
  margin-bottom: 20px;

  .title {
    margin-bottom: 10px;
  }
  .selected {
    font-family: Lato-Bold, Arial, sans-serif;
    color: $secondary-text-color;
  }

  p {
    max-width: 130px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.#{$ns}__labels {
  .campaign-name {
    font-family: Lato-Light, Arial, Helvetica Neue, Helvetica, sans-serif;
    font-size: 26px;
    line-height: 1.3;
    color: $primary-color;
    margin-bottom: 10px;
    white-space: nowrap;
    display: flex;
    align-items: center;

    & > * {
      display: inline-block;
      text-transform: capitalize;
    }

    .selected {
      font-family: Lato-Regular, Arial, Helvetica, "sans-serif";
      max-width: 60%;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .template-item {
    font-size: 14px;
    font-family: Lato-Light, Arial, Helvetica Neue, Helvetica, sans-serif;
    color: $secondary-text-color;
    display: flex;
    align-items: center;

    &.deleted {
      pointer-events: none;
    }
  }
}

.#{$ns}__tags-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  #tags {
    display: flex;
    flex-wrap: wrap;
    height: 30px;
    & > * {
      flex-shrink: 0;
    }
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    font-size: 14px;
    font-family: Lato-Light, Arial, Helvetica Neue, Helvetica, sans-serif;
    line-height: 1.2;
    color: $secondary-text-color;
    margin-top: 20px;
  }

  #hidden-tags {
    white-space: nowrap;
    padding-bottom: 9px;
    padding-left: 3px;
    color: $primary-grey;
    font-size: 12px;
  }
}

/*Media*/

@media screen and (max-width: 991px) {
  .#{$ns}__wrapper {
    width: 100%;
    margin-bottom: 30px;
    margin-right: 0;
  }
}

@media screen and (max-width: 600px) {
  .#{$ns}__mail-info {
    font-size: 10px;
  }
  .#{$ns}__labels {
    .campaign-name {
      font-size: 20px;
    }
  }
}
</style>
<style lang="scss">
.campaign-details {
  display: block;
  margin-left: 5px;
  line-height: 1.2;
  @include textCropEllipsis();
}
</style>
